import { useEffect, useMemo, useRef, useState } from 'react';
import type { Feed, UIComponent } from '~/services/layout/index.ts';
import { useClientFeedApiEndpoint } from '~/hooks/use-client-feed-api-endpoint';
import { useClientFeed } from '~/hooks/use-client-feed';
import { isEnableDataRefreshing } from '~/services/ui-components';
import { useSubscribeToActionRefreshComponent } from '~/hooks/use-subscribe-to-action-refreshComponent';
import { useLoaderData } from '@remix-run/react';
import type { loader as rootLoader } from '~/routes/_index.tsx';

function getFeedEntryCount(feedToCount: Feed | null | undefined): number {
  return feedToCount?.entry?.length ?? 0;
}

export type ClientFeedWrapperRenderProps = {
  feed: Feed | null | undefined;
  isClient: boolean;
  isLoading: boolean;
};

export type ClientFeedWrapperProps = {
  uiComponent: UIComponent;
  feed?: Feed | null | undefined;
  forceFresh?: boolean;
  render: (props: ClientFeedWrapperRenderProps) => JSX.Element;
};

/**
 * Handles fetching and refreshing feed data on the client while supporting SSR-provided data.
 */
export function ClientFeedWrapper({
  uiComponent,
  feed,
  forceFresh,
  render,
}: ClientFeedWrapperProps): JSX.Element {
  const { isTabsScreen } = useLoaderData<typeof rootLoader>();

  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const fetchDataRef = useRef<((forceFresh?: boolean) => Promise<void>) | null>(
    null
  );
  const lastFetchedEndpointRef = useRef<string | null>(null);
  const previousClientFeedApiEndpointRef = useRef<string | null>(null);

  const [isClient, setIsClient] = useState<boolean>(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  const uiComponentDataSource: string | undefined = uiComponent?.data?.source;
  const isServerLoadedFeed: boolean = feed !== undefined;
  const hasServerProvidedFeedEntries: boolean = Boolean(feed?.entry?.length);

  const {
    clientFeedApiEndpoint,
    hasClientContextInRequest,
    feedRequiresClientContext,
  } = useClientFeedApiEndpoint({
    uiComponent,
    applyClientContext: !isServerLoadedFeed,
  });

  const shouldWaitForClientContextBeforeFetch: boolean =
    feedRequiresClientContext && !hasClientContextInRequest;
  const isWaitingForClientContext: boolean =
    shouldWaitForClientContextBeforeFetch;

  const { feedState, fetchData } = useClientFeed(clientFeedApiEndpoint, {
    uiComponentId: uiComponent.id,
    initialData: feedRequiresClientContext ? null : feed ?? null,
  });

  // sync fetchData ref, prevents re-rendering effects when fetchData changes
  fetchDataRef.current = fetchData;

  // sync feedState ref, prevents re-rendering effects when feedState changes
  const feedStateRef = useRef(feedState);
  feedStateRef.current = feedState;

  useSubscribeToActionRefreshComponent({
    clientFeedApiEndpoint,
    fetcherData: feedState.stableData,
    fetchData,
    uiComponent,
  });

  const refreshInterval = uiComponent?.rules?.refreshing_interval;
  const hasRefreshEnabled: boolean =
    isEnableDataRefreshing(uiComponent) && !!refreshInterval;

  // Refetch when the client feed endpoint changes (e.g. context tags applied).
  useEffect(() => {
    if (!uiComponentDataSource || !clientFeedApiEndpoint) return;

    const previousEndpoint: string | null =
      previousClientFeedApiEndpointRef.current;

    previousClientFeedApiEndpointRef.current = clientFeedApiEndpoint;

    if (
      previousEndpoint === null ||
      previousEndpoint === clientFeedApiEndpoint
    ) {
      return;
    }

    lastFetchedEndpointRef.current = null;

    if (fetchDataRef.current) {
      void fetchDataRef.current(true);
    }
  }, [
    clientFeedApiEndpoint,
    uiComponentDataSource,
    hasClientContextInRequest,
    hasServerProvidedFeedEntries,
  ]);

  // Initial fetch - when endpoint changes or when we don't have data
  useEffect(() => {
    if (shouldWaitForClientContextBeforeFetch) return;

    if (
      !uiComponentDataSource ||
      !clientFeedApiEndpoint ||
      feedStateRef.current.state !== 'idle' ||
      lastFetchedEndpointRef.current === clientFeedApiEndpoint ||
      (!isTabsScreen &&
        (feedStateRef.current.data ||
          feedStateRef.current.stableData ||
          hasServerProvidedFeedEntries) &&
        !hasClientContextInRequest &&
        !feedRequiresClientContext)
    )
      return;

    lastFetchedEndpointRef.current = clientFeedApiEndpoint;
    if (fetchDataRef.current) {
      fetchDataRef.current(!!forceFresh);
    }
  }, [
    uiComponentDataSource,
    clientFeedApiEndpoint,
    forceFresh,
    isTabsScreen,
    hasClientContextInRequest,
    feedRequiresClientContext,
    hasServerProvidedFeedEntries,
    shouldWaitForClientContextBeforeFetch,
  ]);

  // Interval for refreshing
  useEffect(() => {
    if (!hasRefreshEnabled || !clientFeedApiEndpoint) {
      return;
    }

    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    intervalRef.current = setInterval(() => {
      // Hook's isLoadingRef guard will prevent concurrent requests
      if (fetchDataRef.current) {
        fetchDataRef.current(true);
      }
    }, refreshInterval * 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [hasRefreshEnabled, clientFeedApiEndpoint, refreshInterval]);

  const feedToRender = useMemo(() => {
    if (isWaitingForClientContext) {
      return undefined;
    }

    const resolvedClientFeed: Feed | null | undefined =
      feedState.state === 'loading'
        ? feedState.stableData ?? feedState.data
        : feedState.data ?? feedState.stableData;

    if (feedRequiresClientContext) {
      return resolvedClientFeed;
    }

    const serverEntryCount: number = getFeedEntryCount(feed);
    const clientEntryCount: number = getFeedEntryCount(resolvedClientFeed);

    const shouldUseServerFeedWhileLoading: boolean =
      feedState.state === 'loading' &&
      serverEntryCount > 0 &&
      clientEntryCount === 0;

    if (shouldUseServerFeedWhileLoading) {
      return feed;
    }

    if (hasServerProvidedFeedEntries && clientEntryCount === 0) {
      return feed;
    }

    return resolvedClientFeed ?? feed;
  }, [
    feedState.data,
    feedState.stableData,
    feedState.state,
    feed,
    feedRequiresClientContext,
    hasServerProvidedFeedEntries,
    isWaitingForClientContext,
  ]);

  const isLoading = useMemo(() => {
    if (isWaitingForClientContext) {
      return true;
    }

    const hasExistingFeedEntries: boolean =
      getFeedEntryCount(feedState.stableData) > 0;

    if (
      hasRefreshEnabled &&
      feedState.state === 'loading' &&
      hasExistingFeedEntries
    ) {
      return false;
    }

    return hasRefreshEnabled && !feedState.data && feedState.stableData
      ? false
      : hasRefreshEnabled
      ? feedState.state !== 'idle'
      : feedState.state !== 'idle' && !feedState.data;
  }, [
    hasRefreshEnabled,
    feedState.data,
    feedState.stableData,
    feedState.state,
    isWaitingForClientContext,
  ]);

  const renderProps = useMemo<ClientFeedWrapperRenderProps>(
    () => ({
      feed: feedToRender,
      isClient,
      isLoading,
    }),
    [feedToRender, isClient, isLoading]
  );

  return render(renderProps);
}
